import { Component } from "@angular/core";
import { ProjectService } from "../shared/services/project.service";
import { Router } from "@angular/router";
import { Field } from "../models/field";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { Observable, pipe, Subject } from "rxjs";
import { take, takeUntil, tap } from "rxjs/operators";
import { CachedProjectService } from "../shared/services/cached-project.service";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "app-job-ouput-route",
    templateUrl: "./job-ouput-route.component.html",
    styleUrls: ["./job-ouput-route.component.less"],
    standalone: false
})
export class JobOuputRouteComponent {
  private destroy$ = new Subject<void>();

  output_path: string | null = null;
  job_id: string | null = null;
  projectModalVisible: boolean = false;
  path_exists: boolean = false;
  maxIterations: number = 0;

  fields: Field[] = [];
  projects: any[] = [];
  projectJobs: any[] = [];

  isCreatingProject: boolean = false;
  isFetchingProjects: boolean = false;
  isFetchingFields: boolean = false;
  isFetchingProjectJobs: boolean = false;
  isCheckingPath: boolean = false;

  projectForm: FormGroup = null;
  newJobForm: FormGroup = null;
  creatingJob: boolean;
  error_message: string =
    "The specified output path does not exist or is not accessible.";

  constructor(
    private projectService: ProjectService,
    private cachedProjectService: CachedProjectService,
    private router: Router,
    private fb: FormBuilder,
    private notificationService: NzNotificationService
  ) {
    const urlParams = new URLSearchParams(window.location.search);
    this.output_path = urlParams.get("output_path");
    console.log("output path", this.output_path);

    this.projectForm = this.fb.group({
      name: ["", [Validators.required]],
      description: [""],
      archiveEnabled: [false],
      isParameterScan: [false],
      field: [null],
    });

    let formatted_output_path = this.output_path;
    if (formatted_output_path?.startsWith("s3://")) {
      formatted_output_path = formatted_output_path.slice(5);
    }
    if (formatted_output_path?.endsWith("/")) {
      formatted_output_path = formatted_output_path.slice(0, -1);
    }
    if (!formatted_output_path?.startsWith("/")) {
      formatted_output_path = "/" + formatted_output_path;
    }

    var path_sections = formatted_output_path.split("/");
    var job_name: string;
    if (path_sections[path_sections.length - 1] !== "output") {
      job_name = path_sections[path_sections.length - 1];
    } else if (path_sections[path_sections.length - 2]) {
      job_name = path_sections[path_sections.length - 2];
    } else {
      job_name = "";
    }

    this.newJobForm = this.fb.group({
      output_path: [
        { value: formatted_output_path, disabled: true },
        [Validators.required],
      ],
      job_name: [job_name, [Validators.required]],
      project: [null, [Validators.required]],
      parentCpNum: [{ value: 0, disabled: true }, [Validators.min(0)]],
      parentJob: [{ value: null, disabled: true }],
      x2_is_inline: [false],
    });

    this.newJobForm
      .get("project")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (p: { id: string }) => {
          this.getProjectJobs(p.id);
        },
        error: (error) => {
          console.error("error", error);
        },
      });

    this.newJobForm
      .get("parentJob")
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          let parentCpNumControl = this.newJobForm.get("parentCpNum");
          if (value) {
            this.maxIterations = value.iterations_completed;
            parentCpNumControl.enable();
            parentCpNumControl.addValidators(Validators.required);
            parentCpNumControl.addValidators(
              Validators.max(value.iterations_completed)
            );
          } else {
            this.maxIterations = 0;
            parentCpNumControl.disable();
            parentCpNumControl.setValue(0);
            parentCpNumControl.removeValidators(Validators.required);
          }
        },
        error: (error) => {
          console.error("error", error);
        },
      });

    this.getAllProjects();

    this.isFetchingFields = true;
    this.cachedProjectService.fields
      .pipe(
        takeUntilDestroyed(),
        tap(() => (this.isFetchingFields = false)),
      )
      .subscribe((fields) => (this.fields = fields));
  }

  ngOnInit() {
    this.isCheckingPath = true;
    this.projectService
      .getJobIdFromPath(this.output_path)
      .toPromise()
      .then((res) => {
        if (res) {
          this.job_id = res?.job_id;
          if (this.job_id) this.router.navigate(["/projects", this.job_id]);
          else {
            this.error_message = res?.message;
            this.path_exists = res?.path_exists;
          }
        } else {
          // console.log("no job id found", res);
          this.error_message = "Data not found in given path.";
          this.job_id = null;
        }
      })
      .catch((err) => {
        // console.error("error getting job id", err.error.error);
        this.error_message = err.error.error
          ? err.error.error
          : "There was an unknown error getting the job data.";
        this.job_id = null;
      })
      .finally(() => {
        this.isCheckingPath = false;
      });
  }

  getAllProjects() {
    this.isFetchingProjects = true;
    this.projectService
      .getAllProjects()
      .toPromise()
      .then((projects) => {
        this.projects = projects;
      })
      .finally(() => {
        this.isFetchingProjects = false;
      });
  }

  createProject() {
    this.isCreatingProject = true;
    this.projectService
      .createProject({ ...this.projectForm.value })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.projectModalVisible = false;
          this.projectForm.reset();
          this.notificationService.success("Success", "project created");
          this.getAllProjects();
          this.isCreatingProject = false;
          this.projectModalVisible = false;
        },
        error: (err) => {
          console.log("error", err);
          this.notificationService.error(
            "Error",
            "Project could not be created due to an error"
          );
          this.isCreatingProject = false;
        },
      });
  }

  getProjectJobs(id: string): void {
    this.isFetchingProjectJobs = true;

    this.cachedProjectService
      ._getProjectJobs(id, {
        fields: ["id", "job_name", "iterations_completed", "project__name"],
      })
      .pipe(take(1))
      .subscribe({
        next: (res) => {
          this.projectJobs = res.data;
          this.newJobForm.get("parentJob").enable();
          this.isFetchingProjectJobs = false;
        },
        error: (err) => {
          console.error("error getting project information and jobs", err);
          this.isFetchingProjectJobs = false;
        },
      });
  }

  createNewJob() {
    if (this.newJobForm.valid) {
      let project = this.newJobForm.get("project").value;
      let formValue = this.newJobForm.getRawValue();
      let entries = [
        {
          path: formValue["output_path"],
          job_name: formValue["job_name"],
          parent_job_id: formValue["parentJob"]
            ? formValue["parentJob"]["id"]
            : null,
          parent_cp_num: formValue["parentCpNum"] || null,
          x2_is_inline: formValue["x2_is_inline"],
          is_update: false,
        },
      ];

      this.creatingJob = true;
      this.projectService
        .createJobs(project.id, entries)
        .pipe(take(1))
        .subscribe({
          next: (res: { failed: Array<any>; passed: Array<any> }) => {
            res["failed"].forEach((element) => {
              this.notificationService.error(
                "Error",
                `Job with path: <div>${element["path"]} </div> <br> failed to be added to the project: <br><b>${element["error"]}</b>`
              );
            });

            res["passed"].forEach((element) => {
              let warnings = element["warnings"] || [];
              warnings.forEach((warning: string) => {
                this.notificationService.warning("Warning", warning);
              });

              this.notificationService.success(
                "Success",
                `Job <div><b>${element["path"]}</b></div> was successfully added to the project`
              );

              this.redirectToJob(element["job"]["id"]);
            });

            this.creatingJob = false;
          },
          error: (err) => {
            this.notificationService.error(
              "Error",
              err.error?.error || "An unexpected error occured"
            );
            this.creatingJob = false;
          },
        });
    } else {
      console.error("Form is invalid");
      this.notificationService.warning(
        "Invalid Form",
        "Please fill in all the required fields."
      );
    }
  }

  redirectToJob(job_id: string) {
    this.router.navigate(["/projects", job_id]);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}

// http://localhost:4201/job?output_path=s3:%2F%2Fprod-5c7d4910-3f75-4157-b22b-81eb50afa962-scube-xwi-showcase%2Foutput%2F1756841879131-68fb7702-783b-4b48-94c0-ae76fe310d7d%2F
// http://localhost:4201/job?output_path=/xwi-cloud-oxy-k2-obn/sc-data/007-inversions/B129-B126cp5-Cont2Hz
